import { Box, Chip, CircularProgress, MenuItem, Modal, TextField, Typography } from '@mui/material';
import { filterOpenFactsCategoryList } from 'common/constants';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import CustomText from 'components/common/CustomText/CustomText';
import { EOpenFactsCategory } from 'enum';
import { ImportPhase, useImportOpenFactsStream } from 'hooks/useBeautyProducts';
import { useEffect, useRef, useState } from 'react';
import { colors } from 'styles/colors';

interface Props {
  open: boolean;
  onClose: () => void;
}

const statusColor: Record<string, string> = {
  suggested: 'success',
  skipped: 'default',
  failed: 'error',
};

const reasonLabel: Record<string, string> = {
  no_images: 'no images',
  insufficient_images: 'insufficient images',
  exists: 'exists',
  quality: 'quality check',
};

export default function ImportOpenFactsDialog({ open, onClose }: Props) {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [category, setCategory] = useState<EOpenFactsCategory>(EOpenFactsCategory.ALL);
  const listRef = useRef<HTMLDivElement>(null);

  const { phase, events, summary, total, error, run, stop, reset } = useImportOpenFactsStream();

  const isRunning = phase === 'running';

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [events.length]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const suggested = summary?.suggested ?? events.filter(e => e.status === 'suggested').length;
  const skipped = summary?.skipped ?? events.filter(e => e.status === 'skipped').length;
  const failed = summary?.failed ?? events.filter(e => e.status === 'failed').length;

  return (
    <Modal open={open} onClose={!isRunning ? handleClose : undefined}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          width: 440,
        }}
      >
        <CustomText text="Auto Import from Open Facts" fontWeight={700} fontSize={18} />

        {/* Input form — shown only before starting */}
        {phase === 'idle' && (
          <>
            <Box mt={2.5} display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Page"
                type="number"
                size="small"
                value={page}
                onChange={e => setPage(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
                fullWidth
              />
              <TextField
                label="Size"
                type="number"
                size="small"
                value={size}
                onChange={e => setSize(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
                fullWidth
              />
              <TextField
                select
                label="Category"
                size="small"
                value={category}
                onChange={e => setCategory(e.target.value as EOpenFactsCategory)}
                fullWidth
              >
                {filterOpenFactsCategoryList.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
              <CustomButton text="Cancel" variant="outlined" intent="primary" height={38} onClick={handleClose} />
              <CustomButton text="Import" height={38} onClick={() => run({ page, size, category })} />
            </Box>
          </>
        )}

        {/* Live feed — shown once started */}
        {phase !== 'idle' && (
          <>
            {/* Counters */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} mb={1.5}>
              <Box display="flex" gap={3}>
                <Counter label="Suggested" value={suggested} color="green" />
                <Counter label="Skipped" value={skipped} color={colors.secondary} />
                <Counter label="Failed" value={failed} color="red" />
              </Box>
              <Typography fontSize={13} color={colors.secondary} fontWeight={500}>
                {summary ? summary.processed : events.length}/{total}
              </Typography>
            </Box>

            {/* Scrollable product list */}
            <Box
              ref={listRef}
              sx={{
                maxHeight: 320,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
              }}
            >
              {events.map((e, i) => (
                <Box
                  key={i}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  py={0.5}
                  px={0.5}
                  sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}
                >
                  <Typography fontSize={13} fontFamily="monospace" color={colors.primary}>
                    {e.barcode}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {e.reason && (
                      <Typography fontSize={11} color={colors.secondary}>
                        {reasonLabel[e.reason] ?? e.reason}
                      </Typography>
                    )}
                    <Chip
                      label={e.status}
                      size="small"
                      color={statusColor[e.status] as any}
                      sx={{ fontSize: 11, height: 20 }}
                    />
                  </Box>
                </Box>
              ))}

              {isRunning && (
                <Box display="flex" justifyContent="center" py={1}>
                  <CircularProgress size={18} />
                </Box>
              )}

              {error && (
                <Typography fontSize={13} color="red" textAlign="center" py={1}>
                  {error}
                </Typography>
              )}
            </Box>

            {/* Summary row when done */}
            {summary && (
              <Typography fontSize={13} color={colors.secondary} mt={1.5} textAlign="right">
                Done — {summary.processed} processed
              </Typography>
            )}

            <Box display="flex" justifyContent="flex-end" gap={1.5} mt={2}>
              {isRunning && (
                <CustomButton
                  text="Stop"
                  variant="outlined"
                  intent="primary"
                  height={38}
                  onClick={stop}
                />
              )}
              <CustomButton
                text="Close"
                height={38}
                disabled={isRunning}
                onClick={handleClose}
              />
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}

function Counter({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Box textAlign="center">
      <Typography fontSize={20} fontWeight={700} color={color}>{value}</Typography>
      <Typography fontSize={12} color={colors.secondary}>{label}</Typography>
    </Box>
  );
}
